import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideStore } from '@ngrx/store';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { FormGroupDirective } from '@angular/forms';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ErrorHandler,
  LOCALE_ID,
  DEFAULT_CURRENCY_CODE,
  isDevMode,
  importProvidersFrom,
  ApplicationConfig,
  provideZoneChangeDetection,
  Type,
} from '@angular/core';
import { CustomLoader } from './translate.loader';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  Route,
  Routes,
  TitleStrategy,
  withComponentInputBinding,
  withViewTransitions,
} from '@angular/router';

import { ErrorHandlerService } from '@application/errors/data-access';
import {
  LUXON_ISO_INITIALIZER,
  LUXON_ZONE_INITIALIZER,
  SERVER_CLOCK_INITIALIZER,
} from '@application/data-access';
import {
  AppConfig,
  AppConfigProvider,
  devExtremeKey,
  APP_ACCOUNT_LOGO_LAYOUT,
  AccountLogoLayoutConfig,
  APP_NAVBAR_LOGO_COMPONENT,
  APP_BIOMETRIC_LOGIN,
  sentryDsn,
} from '@application/config';
import { BreadcrumbTitleStrategy } from '@shared/components/breadcrumb/data-access';
import { LANGUAGE_INITIALIZER } from '@settings/language/data-access';
import { defaultLanguage } from '@shared/utils/language-loader';
import config from 'devextreme/core/config';
import { provideLottieOptions } from 'ngx-lottie';
import {
  LanguageInterceptor,
  LoadingBarInterceptor,
  TokenInterceptor,
  ForbiddenInterceptor,
  DateInterceptor,
  SentryHttpErrorInterceptor,
  PendingMutationInterceptor,
  OfflineRetryInterceptor,
} from '@application/http-client';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { LoggerConfig } from '@shared/utils';
import { isTrackingAllowed } from '@shared/tracking-consent';
import * as Sentry from '@sentry/angular';

// Sentry is initialized dynamically inside appConfig to respect the environment setting.

registerLocaleData(localePt, 'pt-BR');

config({ licenseKey: devExtremeKey });

export function LoaderFactory() {
  return new CustomLoader();
}

export type AppConfigOptions = {
  useLottie?: boolean;
  navbarLogoComponent?: Type<unknown>;
  accountLogoLayout?: AccountLogoLayoutConfig;
  /** Opt into the native biometric-login feature (fingerprint / Face ID unlock). */
  biometricLogin?: boolean;
  /** Gate Sentry on the ATT answer. Only for apps that present the prompt. */
  trackingConsentGate?: boolean;
};

/**
 * Build-time constant, replaced by esbuild through the `define` the CI writes
 * into the build configuration. It does not exist in a local build, which is
 * what the `typeof` guard is for — reading an undeclared identifier throws, and
 * a `ReferenceError` while configuring the app would take the whole bootstrap
 * with it.
 */
declare const NG_SENTRY_RELEASE: string;

function buildRelease(): string | undefined {
  return typeof NG_SENTRY_RELEASE === 'string' && NG_SENTRY_RELEASE
    ? NG_SENTRY_RELEASE
    : undefined;
}

export const appConfig = (
  environment: AppConfig,
  ProvideRouter: Routes | Route[],
  optionalConfig?: AppConfigOptions
): ApplicationConfig => {
  LoggerConfig.init();

  Sentry.init({
    dsn: sentryDsn,
    enabled: environment.environment === 'prod',
    // Without a release every stack trace in Sentry is the minified bundle:
    // there is nothing to match the uploaded source maps against, and an issue
    // reads `chunk-XFVPUX6I.js:113:3389` instead of a file and a line. The value
    // is the same `{run_id}-{sha}` the deploy uses for `/releases/…/`, so the
    // release on an issue and the path its chunks were served from are the same
    // string, and either one finds the other.
    release: buildRelease(),
    // Sentry defaults this to `production` when unset, which happened to be
    // right only because the SDK is enabled in prod alone. Saying it out loud
    // keeps it right the day someone enables a second environment.
    environment: environment.environment,
    // Drop noise injected by the user's browser/extensions, not our code.
    // DuckDuckGo and other WKWebView-based browsers inject a native bridge
    // that rejects postMessage promises when no native handler replies; the
    // rejection surfaces as an unhandled rejection attributed to whatever
    // page was open (Sentry THE-NEW-1C).
    //
    // The Instagram/Facebook in-app browser (Android) injects an `iabjs://`
    // telemetry script that calls native Java objects. When the WebView is
    // backgrounded/torn down the Java object is already released, so a late
    // keyboard-logging flush throws "Java object is gone" — external to our
    // app (Sentry THE-NEW-M7).
    //
    // On iOS the same class of noise arrives as
    // "window.webkit.messageHandlers": an in-app browser (the Google app's)
    // injects a user-script replacing a native API — `window.print` in Sentry
    // THE-NEW 7719496753 — with a bridge to the host app, and the matching
    // message handler is not registered, so the property is undefined and our
    // call throws.
    ignoreErrors: [
      'WKWebView API client did not respond to this postMessage',
      'window.webkit.messageHandlers',
      'Java exception was raised during method invocation',
      'Java object is gone',
      'enableDidUserTypeOnKeyboardLogging',
      /^ResizeObserver loop/,
      'Non-Error promise rejection captured',
    ],
    // Discard events whose top frame is a native-bridge script injected by an
    // in-app browser (Instagram/Facebook `iabjs://`), never our own bundles.
    denyUrls: [/iabjs:\/\//],
    beforeSend: (event) =>
      optionalConfig?.trackingConsentGate && !isTrackingAllowed() ? null : event,
  });

  const configProvider = {
    providers: [
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideHttpClient(
        withInterceptors([
          // First in the chain: the request only counts as finished once every
          // downstream interceptor is done with it (including a token refresh
          // retry), which is exactly when it is safe to leave the page.
          PendingMutationInterceptor,
          // Above `TokenInterceptor`: a read held through a connectivity drop is
          // replayed by re-entering the chain from here, so it goes out with a
          // token refreshed during the outage instead of the one it captured
          // before it.
          OfflineRetryInterceptor,
          LanguageInterceptor,
          ForbiddenInterceptor,
          TokenInterceptor,
          LoadingBarInterceptor,
          DateInterceptor,
          SentryHttpErrorInterceptor,
        ])
      ),
      AppConfigProvider(environment),
      provideStore(),
      provideRouter(
        ProvideRouter,
        withComponentInputBinding(),
        withViewTransitions()
      ),
      {
        provide: TitleStrategy,
        useClass: BreadcrumbTitleStrategy,
      },

      provideAnimations(),
      provideStoreDevtools({
        maxAge: 25,
        logOnly: !isDevMode(),
        autoPause: true,
        trace: false,
        traceLimit: 75,
        connectInZone: true,
      }),
      importProvidersFrom(LoadingBarHttpClientModule),
      importProvidersFrom(
        TranslateModule.forRoot({
          isolate: true,
          defaultLanguage: defaultLanguage,
          loader: {
            provide: TranslateLoader,
            useFactory: LoaderFactory,
          },
        })
      ),
      LANGUAGE_INITIALIZER,
      SERVER_CLOCK_INITIALIZER,
      LUXON_ZONE_INITIALIZER,
      LUXON_ISO_INITIALIZER,
      {
        provide: ErrorHandler,
        useClass: ErrorHandlerService,
      },
      {
        provide: LOCALE_ID,
        useValue: 'pt-BR',
      },
      {
        provide: DEFAULT_CURRENCY_CODE,
        useValue: 'BRL',
      },
      FormGroupDirective,
      provideEnvironmentNgxMask(),
      // QuillConfigModule moved to lazy loading - only load when Quill components are used
      // importProvidersFrom(
      //   QuillConfigModule.forRoot({})
      // )
    ],
  };

  if (optionalConfig?.useLottie) {
    const playerFactory = () => import('lottie-web');
    configProvider.providers.push(
      provideLottieOptions({
        player: playerFactory,
      })
    );
  }

  if (optionalConfig?.navbarLogoComponent) {
    configProvider.providers.push({
      provide: APP_NAVBAR_LOGO_COMPONENT,
      useValue: optionalConfig.navbarLogoComponent,
    });
  }

  if (optionalConfig?.accountLogoLayout) {
    configProvider.providers.push({
      provide: APP_ACCOUNT_LOGO_LAYOUT,
      useValue: optionalConfig.accountLogoLayout,
    });
  }

  if (optionalConfig?.biometricLogin) {
    configProvider.providers.push({
      provide: APP_BIOMETRIC_LOGIN,
      useValue: true,
    });
  }

  return configProvider;
};
